import React, { useState, useEffect, useRef } from 'react';
import Header from '../Header/Header';
import { Icon, CustomTable, Search, Pagination, Loader, ICONS, Select } from '@ohif/ui';
import { useTheme } from "../../ui/src/contextProviders";
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { customStyle, formatDate, formatTime } from '@custom/Utils/utils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DatePickerWithRange } from '@ohif/ui-next';
import { Form, Formik } from 'formik';
import teleradiologistAuditLogsApi from '@custom/rtk-apis/audit/teleradiologistAuditLogs';
import { _studyReviewStatus } from '@custom/Utils/constants/appConstants';
import './index.css';
import AuditFilter from './components/AuditFilter';

// Add proper type definitions
interface QueryParams {
    hospitalId: string;
    limit: number;
    pageNumber: number;
    search: string;
    dateRange?: string;
    startDate?: string;
    endDate?: string;
    [key: string]: any; // To allow additional dynamic properties
}

interface AuditResponseResult {
    totalCount: number;
    data: Array<any>;
    [key: string]: any;
}

interface AuditResponse {
    status: string;
    result: AuditResponseResult;
    loading: boolean;
    msg: string;
    error: string;
    [key: string]: any;
}

const TeleradiologistAuditLogs = () => {
    // Use unknown first to safely cast to our custom type
    const teleradiologistAuditLogsResponse = useSelector((state: RootState) => state.teleradiologistAuditLogsResponse) as unknown as AuditResponse;
    const dispatch = useDispatch<AppDispatch>();
    const { isDarkMode } = useTheme();

    const [searchQuery, setSearchQuery] = useState('');
    const [tableData, setTableData] = useState([]);
    const [pgNumber, setPgNumber] = useState(1);
    const [pgLimit, setPgLimit] = useState(30);
    const [search, setSearch] = useState('');
    const [totalPg, setTotalPg] = useState(100);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
        startDate: null,
        endDate: null,
    });
    const [selectedTimeFrame, setSelectedTimeFrame] = useState(null);
    const [selectedReportStatus, setSelectedReportStatus] = useState(null);

    const columns = [
        { key: 'name', title: 'Name', sort: true },
        { key: 'patientId', title: 'Patient ID' },
        { key: 'gender', title: 'Gender' },
        // { key: 'uhid', title: 'UHID' },
        { key: 'assignTime', title: 'Assign time', sort: true },
        { key: 'assignDate', title: 'Assign date', sort: true },
        { key: 'priority', title: 'Priority' },
        { key: 'acceptTime', title: 'Accept Time' },
        { key: 'rejectionTime', title: 'Rejection Time' },
        { key: 'assignedTo', title: 'Assigned To' },
        { key: 'cancellationReason', title: 'Cancellation Reason' },
        { key: 'authorizationTime', title: 'Auth Time' },
        { key: 'authDate', title: 'Auth Date' },
        { key: 'reportStatus', title: 'Report Status' },
        { key: 'status', title: 'Status' },
    ];
    const actions = [];

    const searchClick = (searchText) => {
        setSearch(searchText);
        setSearchInputValue(searchText);
        fetchAuditData({ search: searchText });
    };

    const onPageChangeHandler = (pg) => {
        setPgNumber(pg);
        fetchAuditData({ pageNumber: pg });
    };

    const handlePageSizeChange = (e) => {
        setPgLimit(e);
        fetchAuditData({ limit: e });
    };

    const resetFilter = () => {
        setSearch('');
        setSearchInputValue('');
        setDateRange({ startDate: '', endDate: '' });
        setSelectedReportStatus(null);
        setSelectedTimeFrame(null);
        setPgNumber(1);
        fetchAuditData({});
    };

    const handleResetForm = () => {
        setSelectedTimeFrame(null);
        setPgNumber(1);
        fetchAuditData({});
    }

    const handleSaveForm = (values) => {
        fetchAuditData({ dateRange: selectedTimeFrame?.value, reportStatus: selectedReportStatus?.value });
    };

    const handleDateChange = (dates: { startDate: string, endDate: string }) => {
        const { startDate, endDate } = dates;
        if (startDate && endDate) {
            setDateRange({ startDate, endDate });
            setSelectedTimeFrame(null); // Clear timeframe when date range is selected
            fetchAuditData({ startDate, endDate });
        }
    };

    const modifyDate = (date?: string) => {
        if (!date) return "";
        // Transform "DDMMYYYY" to "YYYY-MM-DD"
        return `${date?.slice(4, 8)}-${date?.slice(2, 4)}-${date?.slice(0, 2)}`;
    };

    const fetchAuditData = (params = {}) => {
        const hostpialId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        const queryParams: QueryParams = {
            hospitalId: hostpialId,
            limit: pgLimit,
            pageNumber: pgNumber,
            search: search,
            ...params, // Override with any additional params
        };

        if (Object.keys(params).length > 0 && selectedTimeFrame && !dateRange.startDate && !dateRange.endDate) {
            queryParams.dateRange = selectedTimeFrame.value;
        } else if (queryParams.startDate && queryParams.endDate) {
            const changedStartDate = modifyDate(queryParams.startDate);
            const changedEndDate = modifyDate(queryParams.endDate);
            queryParams.startDate = changedStartDate;
            queryParams.endDate = changedEndDate;
        }

        dispatch(teleradiologistAuditLogsApi(queryParams));
    };

    const downloadAuditPDF = () => {
        const doc = new jsPDF();
        doc.text(`Audit Trails (${selectedTimeFrame ? selectedTimeFrame.label : dateRange.startDate && dateRange.endDate ? `${modifyDate(dateRange.startDate)} to ${modifyDate(dateRange.endDate)}` : `Overall`})`, 14, 15);
        const pdfColumns = columns.map(col => col.title);
        const pdfData = tableData.map(row => columns.map(col => row[col.key]));
        (doc as any).autoTable({
            head: [pdfColumns],
            body: pdfData,
            startY: 20,
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: { fillColor: [41, 128, 185] },
        });
        doc.save('audit_trails.pdf');
    };

    const handleDownload = async () => {
        const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        let obj: any = {
            hospitalId,
            limit: 100000,
            pageNumber: 1,
            search: search,
            from: "DOWNLOAD",
        };
        if (selectedTimeFrame && !dateRange.startDate && !dateRange.endDate) {
            obj.dateRange = selectedTimeFrame.value;
        } else if (!selectedTimeFrame && dateRange.startDate && dateRange.endDate) {
            const changedStartDate = modifyDate(dateRange?.startDate);
            const changedEndDate = modifyDate(dateRange?.endDate);
            obj.startDate = changedStartDate;
            obj.endDate = changedEndDate;
        }
        await dispatch(teleradiologistAuditLogsApi(obj));
        if (teleradiologistAuditLogsResponse.status === 'success') {
            downloadAuditPDF();
        }
    };

    const captalizeFirstWord = (str) => {
        str = str?.toLowerCase();
        if (str) {
            return str?.charAt(0)?.toUpperCase() + str?.slice(1);
        } else {
            return str;
        }
    }

    function separateDateTime(utcTime) {
        const localDate = new Date(utcTime);

        const date = localDate.toLocaleDateString();
        const time = localDate.toLocaleTimeString();

        return { date, time };
    }

    useEffect(() => {
        if (teleradiologistAuditLogsResponse.status === 'success') {
            const result = teleradiologistAuditLogsResponse?.result || {};
            setTotalPg(result?.totalCount || 0);

            const rawData = Array.isArray(result?.data) ? result.data : [];

            const flattenObjectIfNested = (item) => {
                if (typeof item !== 'object' || item === null) return {};
                const keys = Object.keys(item);
                const numericKeys = keys.filter(k => !isNaN(Number(k)));

                if (numericKeys.length) {
                    const flatObject = { ...item, ...item[numericKeys[0]] };
                    delete flatObject[numericKeys[0]];
                    return flatObject;
                }
                return item;
            };

            const safeCapitalize = (str) =>
                typeof str === 'string'
                    ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
                    : '';

            const formatName = (name) =>
                typeof name === 'string'
                    ? name.split(' ').map(w => safeCapitalize(w)).join(' ')
                    : '--';

            const tableArray = rawData.map((itemRaw) => {
                const item = flattenObjectIfNested(itemRaw);
                const assignedTime = item?.assigned_time ? separateDateTime(item?.assigned_time) : {};
                const acceptanceTime = item?.acceptance_time ? separateDateTime(item?.acceptance_time) : {};
                const rejectionTime = item?.rejection_time ? separateDateTime(item?.rejection_time) : {};
                const authorizationTime = item?.authorizationTime ? separateDateTime(item?.authorizationTime) : {};

                return {
                    name: formatName(item?.patientName),
                    patientId: item?.patientId || '--',
                    gender:
                        item?.gender === 'M'
                            ? 'Male'
                            : item?.gender === 'F'
                            ? 'Female'
                            : item?.gender || '--',
                    assignTime: assignedTime?.time || '--',
                    assignDate: assignedTime?.date || '--',
                    priority: item?.priority || '--',
                    acceptTime:
                        item?.status === _studyReviewStatus.ACCEPTED
                            ? acceptanceTime?.time || '--'
                            : '--',
                    authorizationTime: authorizationTime?.time || '--',
                    authDate: authorizationTime?.date || '--',
                    rejectionTime:
                        item?.status === _studyReviewStatus.REJECTED
                            ? rejectionTime?.time || '--'
                            : '--',
                    cancellationReason:
                        item?.status === _studyReviewStatus.REJECTED
                            ? item?.cancellationReason || '--'
                            : '--',
                    assignedTo: formatName(item?.assignedRadiologistName),
                    reportStatus: item?.reportStatus || '--',
                    status:
                        item?.status === ''
                            ? 'Pending'
                            : item?.cancellationReason?.includes('Auto rejected by the system')
                            ? 'Auto Rejected'
                            : captalizeFirstWord(item?.status) || '--',
                };
            });

            setTableData(tableArray);
        }
    }, [teleradiologistAuditLogsResponse.status, teleradiologistAuditLogsResponse.result]);

    useEffect(() => {
        fetchAuditData(); // Initial fetch
    }, []);

    return (
        <>
            <div className='page page__fullBody' style={isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}>
                <Header isBack={false} title='Audit Trails'></Header>
                <div className='page__body'>
                    <div className='tableWrap'>
                        <div className='tableWrap__head'>
                            <div className="formRow formRow__3 m_0 w_100 formRow--flexEnd">
                                <div className='formCol'>
                                    <Search text="Name, Assigned To" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue}></Search>
                                </div>
                                <div className='formCol'>
                                    <DatePickerWithRange
                                        id="dateFilter"
                                        className="px-4 py-1 rounded h-10"
                                        onChange={handleDateChange as any}
                                        startPlaceholder="From"
                                        endPlaceholder="To"
                                        startDate={dateRange.startDate}
                                        endDate={dateRange.endDate}
                                        style={{ width: '200px' }}
                                    />
                                </div>
                                <div className="formCol">
                                    <AuditFilter
                                        selectedTimeFrame={selectedTimeFrame}
                                        setSelectedTimeFrame={setSelectedTimeFrame}
                                        selectedReportStatus={selectedReportStatus}
                                        setSelectedReportStatus={setSelectedReportStatus}
                                        onApplyFilter={handleSaveForm}
                                        onResetFilter={handleResetForm}
                                        onClearAll={resetFilter}
                                    />
                                </div>
                            </div>
                            <button className='btn btn__white' style={{ borderRadius: '50px' }} onClick={handleDownload}>
                                Download PDF
                            </button>
                        </div>
                        <div className='tableContainer'>
                            <CustomTable
                                columns={columns}
                                data={tableData}
                                actions={actions}
                                searchQuery={searchQuery}
                                from="teleradiologistAuditLogs"
                                userDetails={''}
                                setClickedItem={''}
                                clickedItem={''}
                            />
                        </div>
                    </div>
                </div>
                {tableData.length > 0 && <Pagination
                    onChangePage={onPageChangeHandler}
                    onChangePerPage={handlePageSizeChange}
                    totalPage={totalPg}
                    currentPage={pgNumber}
                    perPage={pgLimit}
                />}
                <Loader isLoaderOpen={teleradiologistAuditLogsResponse.loading} />
            </div>
        </>
    );
};

export default TeleradiologistAuditLogs;
